/**
 * A bill paid by more than one method.
 *
 * Two people splitting it, or someone paying part on a card and the rest in
 * cash because the card was short. Common enough at a counter that not having
 * it means the cashier rings two orders and the kitchen makes two tickets.
 *
 * THE ARITHMETIC IS THE WHOLE PROBLEM
 * -----------------------------------
 * The parts have to come to the total exactly. A rupee out and the order is
 * recorded as underpaid — which means it never settles, the shift will not
 * reconcile, and someone spends the evening looking for a rupee.
 *
 * So the remaining amount is always shown, and the order cannot be completed
 * until it is nothing.
 */
import { round2, sumMoney } from '@foodrun/shared';

/** What the parts entered so far come to. */
export function paidSoFar(parts) {
  return round2(sumMoney(parts.map((p) => Number(p.amount) || 0)));
}

/**
 * What is still owed.
 *
 * Rounded, because a chain of float additions leaves 0.00000000001 behind and
 * a cashier is then told an order is unpaid by a hundred-billionth of a rupee.
 */
export function remaining(total, parts) {
  return round2(Number(total || 0) - paidSoFar(parts));
}

/**
 * Is this split complete and sendable?
 *
 * Anything within half a paisa counts as settled — the smallest coin in
 * circulation is a rupee, and insisting on exact float equality would refuse
 * splits that are correct to every figure anyone can actually pay.
 */
export function isSettled(total, parts) {
  if (parts.length === 0) return false;
  if (parts.some((p) => !(Number(p.amount) > 0))) return false;
  return Math.abs(remaining(total, parts)) < 0.005;
}

/** The body the backend expects. Only complete parts are sent. */
export function toPayments(parts) {
  return parts
    .filter((p) => Number(p.amount) > 0)
    .map((p) => ({ method: p.method, amount: round2(Number(p.amount)) }));
}
